import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Package, Search } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Edit, ArrowRightLeft } from 'lucide-react';
import { StockAdjustmentForm } from '@/components/inventory/stock-adjustment-form';
import { StockHistorySheet } from '@/components/inventory/stock-history-sheet';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useStockItems, useCreateStockItem } from '@/hooks/use-inventory';
import { useOutlets } from '@/hooks/use-outlets';
import { useToast } from '@/hooks/use-toast';


export const Route = createFileRoute('/inventory')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <InventoryPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function InventoryPage() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState<any>(null);
  const [newItem, setNewItem] = useState({ name: '', unit: '', reorderPoint: '' });
  const [adjustItem, setAdjustItem] = useState<any>(null);
  
  const { data: itemsResponse, isLoading } = useStockItems({ search });
  const items = itemsResponse?.data || [];
  
  const { data: outletsResponse } = useOutlets();
  const outlets = outletsResponse?.data || [];
  
  const createItem = useCreateStockItem();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createItem.mutateAsync({
        ...newItem,
        reorderPoint: newItem.reorderPoint ? parseFloat(newItem.reorderPoint) : null
      });
      setIsCreateOpen(false);
      setNewItem({ name: '', unit: '', reorderPoint: '' });
      toast({ title: 'Success', description: 'Stock item created' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="flex flex-col gap-6 min-w-0 max-w-full">
      <PageHeader
        title="Inventory"
        actions={(
          <Button onClick={() => setIsCreateOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Add Item
          </Button>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{items.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {items.filter(i => i.isActive).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(items.map(i => i.category).filter(Boolean)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Min.</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Loading inventory...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-20 text-muted-foreground">
                    <div className="flex flex-col items-center">
                      <div className="h-16 w-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
                        <Package className="h-8 w-8 opacity-20" />
                      </div>
                      <p className="text-sm font-semibold text-foreground">No stock items found</p>
                      <p className="text-xs max-w-[200px] mt-1">Items you add to your inventory will appear here.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: any) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>
                      <span className={cn(
                        "font-bold",
                        Number(item.levels?.[0]?.qtyOnHand || 0) <= (item.reorderPoint || 0) ? "text-destructive" : "text-emerald-600"
                      )}>
                        {item.levels?.[0]?.qtyOnHand || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground italic text-xs">
                        {item.reorderPoint || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.isActive ? 'success' : 'secondary'}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                       <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                         <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setAdjustItem(item)}>
                            <ArrowRightLeft className="mr-2 h-4 w-4 text-primary" />
                            Adjust Stock
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setHistoryItem(item); setIsHistoryOpen(true); }}>
                            <Search className="mr-2 h-4 w-4 text-primary" />
                            View History
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem disabled>
                            <Edit className="mr-2 h-4 w-4 text-muted-foreground" />
                            Edit details
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Stock Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="e.g., Detergent"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Unit</label>
              <Input
                value={newItem.unit}
                onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                placeholder="e.g., liter, kg, pcs"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reorder Point (Min. Stock)</label>
              <Input
                type="number"
                value={newItem.reorderPoint}
                onChange={(e) => setNewItem({ ...newItem, reorderPoint: e.target.value })}
                placeholder="0"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                You will be notified when stock falls below this level.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createItem.isPending}>
                {createItem.isPending ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjustItem} onOpenChange={(open) => !open && setAdjustItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
          </DialogHeader>
          {adjustItem && (
             <StockAdjustmentForm
                stockItem={adjustItem}
                outletId={outlets?.[0]?.id || ''} 
                onSuccess={() => setAdjustItem(null)}
             />
          )}
        </DialogContent>
      </Dialog>

      <StockHistorySheet 
        item={historyItem} 
        isOpen={isHistoryOpen} 
        onOpenChange={setIsHistoryOpen} 
      />
    </div>
  );
}
